import { openDB, IDBPDatabase } from 'idb';

/**
 * Thin promise wrapper over a single IndexedDB object store that holds the
 * per-conversion filing-form persistence blobs. Sole owner of the DB name,
 * version, and store name — never reference these literals elsewhere.
 *
 * Keys are the exact same strings used historically in localStorage
 * (e.g. `mbrs-filing-form-modified-<id>`), so the key format is unchanged.
 *
 * This class is intentionally DI-free so it can be unit-tested without
 * TestBed and instantiated by the persistence service directly.
 */
export class FilingFormIdb {
  private static readonly DB_NAME = 'mbrs-filing-form';
  private static readonly DB_VERSION = 1;
  private static readonly STORE = 'state';

  private dbPromise: Promise<IDBPDatabase> | null = null;

  private open(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(FilingFormIdb.DB_NAME, FilingFormIdb.DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(FilingFormIdb.STORE)) {
            db.createObjectStore(FilingFormIdb.STORE);
          }
        },
      });
    }
    return this.dbPromise;
  }

  async isAvailable(): Promise<boolean> {
    try {
      await this.open();
      return true;
    }
    catch {
      return false;
    }
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.open();
    const value = await db.get(FilingFormIdb.STORE, key);
    return (value === undefined ? null : value) as T | null;
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.open();
    await db.put(FilingFormIdb.STORE, value, key);
  }

  /**
   * Read-modify-write a key inside a SINGLE readwrite transaction.
   *
   * The transaction is the whole point: `get()` followed by `set()` is a lost
   * update waiting to happen, because another browser tab can commit between
   * the two. IndexedDB serialises overlapping readwrite transactions on the same
   * store, so doing both inside one `tx` makes the merge atomic with respect to
   * every other tab on this origin.
   *
   * `updater` receives `null` when the key is absent, and must return the value
   * to store. Keep it pure and synchronous — awaiting anything inside it would
   * let the transaction auto-close before the `put` lands.
   */
  async update<T>(key: string, updater: (current: T | null) => T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(FilingFormIdb.STORE, 'readwrite');
    const current = await tx.store.get(key);
    const next = updater((current === undefined ? null : current) as T | null);
    await tx.store.put(next, key);
    await tx.done;
  }

  async delete(key: string): Promise<void> {
    const db = await this.open();
    await db.delete(FilingFormIdb.STORE, key);
  }

  async clear(): Promise<void> {
    const db = await this.open();
    await db.clear(FilingFormIdb.STORE);
  }

  async keys(): Promise<string[]> {
    const db = await this.open();
    return (await db.getAllKeys(FilingFormIdb.STORE)) as string[];
  }
}
